import type { PurchaseRecoveryStorage, PurchaseSnapshot } from './types';

const PENDING_TTL_MS = 24 * 60 * 60 * 1000;
const PENDING_LIMIT = 10;
const SENT_LIMIT = 50;

export class PurchaseRecoveryService {
  constructor(private readonly storage: PurchaseRecoveryStorage) {}

  remember(snapshot: PurchaseSnapshot): void {
    const record = this.storage.read();

    if (record.sent.includes(snapshot.orderId)) {
      return;
    }

    const pending = this.withoutExpired(record.pending)
      .filter((item) => item.orderId !== snapshot.orderId)
      .concat(snapshot)
      .slice(-PENDING_LIMIT);

    this.storage.write({ ...record, pending });
  }

  getPending(orderId: string): PurchaseSnapshot | null {
    const record = this.storage.read();

    if (record.sent.includes(orderId)) {
      return null;
    }

    return (
      this.withoutExpired(record.pending).find(
        (item) => item.orderId === orderId,
      ) ?? null
    );
  }

  markSentIfNotYet(orderId: string): boolean {
    const record = this.storage.read();

    if (record.sent.includes(orderId)) {
      return false;
    }

    this.storage.write({
      pending: this.withoutExpired(record.pending).filter(
        (item) => item.orderId !== orderId,
      ),
      sent: [...record.sent, orderId].slice(-SENT_LIMIT),
    });

    return true;
  }

  forget(orderId: string): void {
    const record = this.storage.read();
    const pending = this.withoutExpired(record.pending).filter(
      (item) => item.orderId !== orderId,
    );

    if (pending.length === record.pending.length) {
      return;
    }

    this.storage.write({ ...record, pending });
  }

  private withoutExpired(pending: PurchaseSnapshot[]): PurchaseSnapshot[] {
    const oldestAllowed = Date.now() - PENDING_TTL_MS;
    return pending.filter((item) => item.createdAt >= oldestAllowed);
  }
}
